// Apolo IDE — line-based diff + hunk extraction (Sprint v11 E3).
//
// Pure-client diff algorithm. Splits old/new strings into lines, computes
// the longest common subsequence (LCS), and emits a list of hunks where
// each hunk represents a contiguous run of edits (insert / delete / change)
// with N context lines on either side.
//
// We do not import an LCS package because the existing diff need is small
// (single file, <10k lines is plenty). For massive files Pedro can drop a
// 3rd-party diff in here later — public API (`computeHunks`, `applyHunks`)
// stays stable.

const CONTEXT_LINES = 3;

function splitLines(str) {
  if (typeof str !== 'string') return [];
  if (str === '') return [];
  // Split on \n but preserve no trailing empty if str ends with \n
  const parts = str.split('\n');
  if (parts[parts.length - 1] === '' && str.endsWith('\n')) parts.pop();
  return parts;
}

/** Compute the LCS table and trace operations back to ops. */
function diffOps(oldLines, newLines) {
  const m = oldLines.length;
  const n = newLines.length;

  // dp[i][j] = LCS length of oldLines[0..i] / newLines[0..j]
  const dp = Array.from({ length: m + 1 }, () => new Uint32Array(n + 1));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldLines[i - 1] === newLines[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = dp[i - 1][j] > dp[i][j - 1] ? dp[i - 1][j] : dp[i][j - 1];
      }
    }
  }

  // Walk back to build ops in forward order.
  const ops = []; // { type: 'equal'|'del'|'add', oldIdx, newIdx }
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (oldLines[i - 1] === newLines[j - 1]) {
      ops.push({ type: 'equal', oldIdx: i - 1, newIdx: j - 1 });
      i--; j--;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      ops.push({ type: 'del', oldIdx: i - 1, newIdx: j });
      i--;
    } else {
      ops.push({ type: 'add', oldIdx: i, newIdx: j - 1 });
      j--;
    }
  }
  while (i > 0) {
    ops.push({ type: 'del', oldIdx: i - 1, newIdx: 0 });
    i--;
  }
  while (j > 0) {
    ops.push({ type: 'add', oldIdx: 0, newIdx: j - 1 });
    j--;
  }
  return ops.reverse();
}

/**
 * Group an op stream into hunks. A hunk is a contiguous block of non-equal
 * ops, padded with up to CONTEXT_LINES equal ops on each side. Hunks that
 * overlap on context merge.
 *
 * @param {string} oldText
 * @param {string} newText
 * @returns {Array<{
 *   id: string,
 *   oldStart: number, oldEnd: number,   // 0-based line indices (end exclusive)
 *   newStart: number, newEnd: number,
 *   header: string,                     // "@@ -X,Y +A,B @@"
 *   lines: Array<{ type: 'context'|'del'|'add', text: string, oldLine?: number, newLine?: number }>,
 * }>}
 */
export function computeHunks(oldText, newText) {
  const oldLines = splitLines(oldText);
  const newLines = splitLines(newText);
  const ops = diffOps(oldLines, newLines);
  if (ops.length === 0) return [];

  // Identify segments of consecutive non-equal ops to seed hunks.
  const hunks = [];
  let cursor = 0;
  while (cursor < ops.length) {
    if (ops[cursor].type === 'equal') {
      cursor++;
      continue;
    }
    // Start of an edit segment — walk forward to its end.
    let end = cursor;
    while (end < ops.length && ops[end].type !== 'equal') end++;
    // Greedy merge with the next edit segment if context lines overlap.
    let segmentEnd = end;
    while (
      segmentEnd < ops.length &&
      ops[segmentEnd].type === 'equal' &&
      // Look ahead to see if there is another edit within 2*CONTEXT
      ops.slice(segmentEnd, segmentEnd + CONTEXT_LINES * 2).some((o) => o.type !== 'equal')
    ) {
      // advance through equal lines that are followed by edits
      segmentEnd++;
      while (segmentEnd < ops.length && ops[segmentEnd].type !== 'equal') segmentEnd++;
    }

    const contextStart = Math.max(0, cursor - CONTEXT_LINES);
    const contextEnd = Math.min(ops.length, segmentEnd + CONTEXT_LINES);

    const sliceOps = ops.slice(contextStart, contextEnd);
    if (sliceOps.length === 0) {
      cursor = segmentEnd;
      continue;
    }

    const firstOldIdx = sliceOps.find((o) => o.type !== 'add')?.oldIdx ?? 0;
    const firstNewIdx = sliceOps.find((o) => o.type !== 'del')?.newIdx ?? 0;
    const lastOldOp = [...sliceOps].reverse().find((o) => o.type !== 'add');
    const lastNewOp = [...sliceOps].reverse().find((o) => o.type !== 'del');
    const oldStart = firstOldIdx;
    const oldEndExcl = lastOldOp ? lastOldOp.oldIdx + 1 : firstOldIdx;
    const newStart = firstNewIdx;
    const newEndExcl = lastNewOp ? lastNewOp.newIdx + 1 : firstNewIdx;

    const lines = sliceOps.map((op) => {
      if (op.type === 'equal') {
        return {
          type: 'context',
          text: oldLines[op.oldIdx],
          oldLine: op.oldIdx + 1,
          newLine: op.newIdx + 1,
        };
      }
      if (op.type === 'del') {
        return { type: 'del', text: oldLines[op.oldIdx], oldLine: op.oldIdx + 1 };
      }
      return { type: 'add', text: newLines[op.newIdx], newLine: op.newIdx + 1 };
    });

    hunks.push({
      id: `hunk-${oldStart}-${newStart}`,
      oldStart,
      oldEnd: oldEndExcl,
      newStart,
      newEnd: newEndExcl,
      header: `@@ -${oldStart + 1},${oldEndExcl - oldStart} +${newStart + 1},${newEndExcl - newStart} @@`,
      lines,
    });

    cursor = segmentEnd;
  }
  return hunks;
}

/**
 * Apply a subset of hunks to oldText, returning the new text. Hunks not
 * present in `acceptedIds` are skipped (i.e. their oldLines are kept).
 *
 * @param {string} oldText
 * @param {ReturnType<typeof computeHunks>} hunks
 * @param {Set<string>} acceptedIds
 * @returns {string}
 */
export function applyHunks(oldText, hunks, acceptedIds) {
  const oldLines = splitLines(oldText);
  const accepted = new Set(acceptedIds);
  // Sort hunks by oldStart so we can walk in order.
  const sorted = [...hunks].sort((a, b) => a.oldStart - b.oldStart);
  const result = [];
  let cursor = 0;
  for (const h of sorted) {
    // Copy unchanged old lines before this hunk.
    for (let i = cursor; i < h.oldStart; i++) result.push(oldLines[i]);
    if (accepted.has(h.id)) {
      // Take the new side of the hunk (skip context lines from old that
      // appear inside the hunk — we use the `lines` array's add+context
      // entries which already represent the new side).
      for (const l of h.lines) {
        if (l.type === 'context' || l.type === 'add') result.push(l.text);
      }
    } else {
      // Keep old lines from the hunk untouched.
      for (let i = h.oldStart; i < h.oldEnd; i++) result.push(oldLines[i]);
    }
    cursor = h.oldEnd;
  }
  // Tail
  for (let i = cursor; i < oldLines.length; i++) result.push(oldLines[i]);

  // Preserve trailing newline if oldText had one.
  const joined = result.join('\n');
  return oldText.endsWith('\n') ? joined + '\n' : joined;
}
